import React, { useContext, useState, useRef, memo, useEffect, forwardRef } from "react";
import styles from "./MoreLikeThis.module.scss";
import DeviceContext from "src/contexts/DeviceContext";
import WorkflowContext from "src/contexts/WorkflowContext";
import TranslationsContext from "src/contexts/TranslationsContext";
import { batchGetMesMetadata, getMesMetadata } from "src/utils/asinMetadataUtils";
import BookCoverWithPlaceholder from "../BookCoverWithPlaceholder/BookCoverWithPlaceholder";
import { getProgramSticker } from "src/utils/programLogoUtils";
import { recordBehavioralMetric } from "src/utils/metricsUtils";

type BookImagePropTypes = {
    asin: string;
    index: number;
    count: number;
    shouldScrollIntoView?: boolean;
    onSelfFilterCallback: (asin: string) => void;
    onTapCallback: (index: number) => void;
}

const smoothScrollOptions: ScrollIntoViewOptions = { block: "nearest", inline: "center", behavior: "smooth" };

// MES backend is returning more than 30 for genre tags to handle filtering issues, but for CX consistency we will keep them all to 30
// Ref: https://tiny.amazon.com/4f0g9pmb/codeamazpackBookblobf5cesetu.
const MAX_ASINS_PER_TAG = 30;

const BookImage: React.FC<BookImagePropTypes> = ({ asin, index, count, shouldScrollIntoView, onTapCallback, onSelfFilterCallback }: BookImagePropTypes) => {
    const context = useContext(DeviceContext);
    const translations = useContext(TranslationsContext);
    const itemRef = useRef<HTMLLIElement>(null);
    const [metadata, setMetadata] = useState<QuickViewAsinMetadata>({ asin });
    const [hasFetchedMetadata, setHasFetchedMetadata] = useState(false);

    useEffect(() => {
        if (hasFetchedMetadata) { return; }
        setHasFetchedMetadata(true);
        getMesMetadata(asin)
            .then(data => { setMetadata(data); })
            .finally(() => setHasFetchedMetadata(metadata?.loaded === true));
    }, [asin, hasFetchedMetadata, index, metadata?.loaded]);

    useEffect(() => {
        if (shouldScrollIntoView) {
            itemRef.current?.scrollIntoView(smoothScrollOptions);
        }
    }, [shouldScrollIntoView]);

    // TODO: Consider removing this after Yihan's backend filtering proves successful
    if (metadata.loaded === true && !metadata.isEbook) {
        onSelfFilterCallback(asin);
        return null;
    }

    const stickerCode = getProgramSticker(context.theme, metadata.kindleProgramLowCost, metadata.isAFR);

    return (
        <li ref={itemRef}
            className={[stickerCode ? styles.bookImageWithSticker : "", styles.bookImage].join(" ")}
        >
            <button
                className={styles.coverButton}
                onClick={() => {
                    recordBehavioralMetric({namespace: "MES", qv_asin: asin}, "MES.AsinIndexClicked", index);
                    onTapCallback(index);
                }}
                aria-posinset={index}
                aria-setsize={count}
            >
                <div className={styles.coverWithBadgeWrapper}>
                    {stickerCode && (<div className={metadata.isRPL ? styles.programBadgeRpl : styles.programBadge}>
                    <img src={stickerCode.src} alt={stickerCode?.altText || translations.getText(stickerCode.altKey)} />
                </div>)}
                    <BookCoverWithPlaceholder
                        asin={asin}
                        authors={metadata.authors}
                        title={metadata.title ?? ""}
                        physicalId={metadata.physicalId}
                        imagePxSize={200}
                        imagePxType="height"
                        lazy={index > 3}
                        showTransition
                    />
                </div>
            </button>
        </li>
    );
};

type BookImageShovelerPropsType = {
    asins?: string[];
    title: string;
    tag: string;
    disableScrolling?: boolean;
    scrollToItemIndex: number;
    setScrollToItemIndex: (index: number) => void;
};
const BookImageShoveler = forwardRef<HTMLUListElement, BookImageShovelerPropsType>(function BookImageShoveler({ asins, tag, title, disableScrolling, scrollToItemIndex, setScrollToItemIndex }, ref) {
    const deviceContext = useContext(DeviceContext);
    const workflowContext = useContext(WorkflowContext);
    const translations = useContext(TranslationsContext);
    const selfFilteredAsins = useRef(new Set<string>());
    const [selfFilteredCount, setSelfFilteredCount] = useState(0);
    const [filteredAsins, setFilteredAsins] = useState<string[]>([]);

    // Make batch call to fetch MES metadata
    useEffect(() => {
        batchGetMesMetadata(asins ?? []);
    }, [asins]);

    useEffect(() => {
        setFilteredAsins(
            asins?.filter(asin => !selfFilteredAsins.current.has(asin)) ?? []
        );
    }, [asins, selfFilteredCount]);

    const openQvAtIndex = (index: number) => {
        workflowContext.openQv(
            title,
            filteredAsins?.map(asin => { return { asin: asin }; }) || [],
            index,
            (index: number) => setScrollToItemIndex(index)
        );
    };
    const selfFilterCallback = (asin: string) => {
        selfFilteredAsins.current.add(asin);
        setSelfFilteredCount(selfFilteredAsins.current.size);
    };

    const count = filteredAsins?.length ?? 0;

    const hasItems = count > 0;
    return (
        <ul className={`${styles.horizontalScroller} ${styles[deviceContext.theme]} ${disableScrolling ? styles.disableScrolling : ""} ${styles.fixedHeight} ${!hasItems ? styles.noItems : ""}`}
            role="listbox"
            aria-label={tag}
            ref={ref}
        >
            {hasItems
                ? filteredAsins?.map((asin: string, index: number) =>
                        <BookImage
                            key={asin + index + tag}
                            asin={asin}
                            index={index}
                            count={count}
                            onSelfFilterCallback={selfFilterCallback}
                            onTapCallback={openQvAtIndex}
                            shouldScrollIntoView={scrollToItemIndex === index}
                        />
                    )
                : (<div className={styles.noItemsErrorText}>
                    {translations.getText("no-kindle-books-available")}
                </div>)
            }
        </ul>
    );
});

type PropTypes = {
    title: string;
    mesKey: string;
    disableScrolling?: boolean;
    items: MESRecItem[];
};

const MoreLikeThisMES: React.FC<PropTypes> = ({ title, mesKey, disableScrolling, items }: PropTypes) => {
    const scrollerRef = useRef<HTMLUListElement>(null);
    const sectionRef = useRef<HTMLElement>(null);
    const deviceContext = useContext(DeviceContext);
    const translations = useContext(TranslationsContext);
    const [selectedTagIndex, setSelectedTagIndex] = useState(0);
    const [scrollToItemIndex, setScrollToItemIndex] = useState(-1);
    const [hasImpressed, setHasImpressed] = useState(false);

    const scrollToFirstItem = () => {
        scrollerRef.current?.firstElementChild?.scrollIntoView(smoothScrollOptions);
    };

    useEffect(() => {
        const intersectionObserver = new IntersectionObserver((entries, observer) => {
            if (entries.filter((entry) => entry.isIntersecting).length > 0 && !hasImpressed) {
                recordBehavioralMetric({namespace: "MES"}, "MES.Impressed", 1);
                setHasImpressed(true);
                observer.disconnect();
            }
        }, {
            threshold: 0.5,
        });
        if (sectionRef.current) {
            intersectionObserver.observe(sectionRef.current);
        }
        return () => intersectionObserver?.disconnect();
    }, [sectionRef]);

    const getTagsComponent = () => {
        return (
            <ul
                className={`${styles.horizontalScroller} ${styles.tagsCarousel} ${disableScrolling ? styles.disableScrolling : ""}`}
                role="listbox"
                aria-label={translations.getText("more-like-this")}
            >
                {items.map((item, index) =>
                    <li key={mesKey + (item.tag ?? "") + index}>
                        <button
                            className={[styles.tagsPill, index === selectedTagIndex ? styles.selected : ""].join(" ")}
                            onClick={(event: React.UIEvent) => {
                                recordBehavioralMetric({namespace: "MES"}, "MES.TagIndexClicked", index);
                                // Scroll the tapped tag into view
                                event.currentTarget.scrollIntoView(smoothScrollOptions);
                                setScrollToItemIndex(-1);
                                setSelectedTagIndex(index);
                                scrollToFirstItem();
                            }}
                            aria-selected={index === selectedTagIndex}
                            aria-setsize={items.length}
                            aria-posinset={index}
                        >
                            {item.tag}
                        </button>
                    </li>
                )}
                {/* Add an end spacer so the last tag pill can scroll onto screen without the gradient covering it */}
                <li className={styles.tagsEndSpacer} aria-hidden>&nbsp;</li>
            </ul>
        );
    };

    return (
        <section ref={sectionRef} className={[styles[deviceContext.theme]].join(" ")}>
            <div className={styles.label}>
                {translations.getText("more-like-this")}
            </div>
            {getTagsComponent()}
            <BookImageShoveler
                ref={scrollerRef}
                title={title}
                scrollToItemIndex={scrollToItemIndex}
                setScrollToItemIndex={setScrollToItemIndex}
                asins={items?.[selectedTagIndex].asins?.slice(0, MAX_ASINS_PER_TAG)}
                tag={items?.[selectedTagIndex].tag ?? ""}
            />
        </section>
    );
};

export default memo(MoreLikeThisMES);
